import { Button } from "@/view/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { BasicInput } from "@/view/components/BasicInput";
import { moneyMask } from "@/app/utils/formatMoneyValue";
import { BasicSelect } from "@/view/components/BasicSelect";
import { useQuery } from "@tanstack/react-query";
import { panService } from "@/app/services/panService";
import { usePanMutations } from "@/app/hooks/usePanMutations";
import { useNavigate } from "react-router-dom";
import { LocationState } from "@/view/pages/PanSimulation/usePanSimulation";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { selfHireService } from "@/app/services/selfHireService";

interface ModalSimulatePanProps {
  transactionId: string;
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selfHireSimulate?: (state?: LocationState) => void;
  linkName?: string;
}

export const ModalSimulatePan = ({
  transactionId,
  customerId,
  open,
  onOpenChange,
  selfHireSimulate,
  linkName,
}: ModalSimulatePanProps) => {
  const navigate = useNavigate();
  const { generateOffersMutation } = usePanMutations();
  const { generateOffersPanMutation } = useSelfHiringMutation();

  const [loading, setLoading] = useState(false);

  const isSelfHireFlow = Boolean(selfHireSimulate);

  const { data: panData } = useQuery({
    queryKey: [
      "panDetails",
      transactionId,
      isSelfHireFlow ? "self-hire" : "dashboard",
    ],
    queryFn: () =>
      isSelfHireFlow
        ? selfHireService.getProposalPan(transactionId)
        : panService.getPanId(transactionId),
    enabled: !!transactionId,
  });

  const prazoOptions =
    panData?.deadlines?.map((d) => ({
      label: `${d}x`,
      value: d,
    })) ?? [];

  const getSimulatePanFormSchema = z.object({
    value: z
      .string()
      .min(1, "Valor é obrigatório")
      .refine(
        (val) => {
          if (!panData?.amount) return true;

          const valorDigitado = Number(
            val.replace(/\s|R\$|\./g, "").replace(",", "."),
          );
          const valorMaximo = panData.amount / 100;

          return valorDigitado <= valorMaximo;
        },
        {
          message: `Valor não pode ser maior que ${
            panData?.amount
              ? (panData.amount / 100).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "o valor máximo"
          }`,
        },
      ),
    renda: z.string().optional(),
    prazo: z.string().min(1, "Prazo é obrigatório"),
  });

  type getSimulatePanSchemaData = z.infer<typeof getSimulatePanFormSchema>;

  const form = useForm<getSimulatePanSchemaData>({
    resolver: zodResolver(getSimulatePanFormSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const handleSimulate = handleSubmit(async (formData) => {
    const valorNumber = Number(
      formData.value.replace(/\s|R\$|\./g, "").replace(",", "."),
    );

    const rendaNumber = formData.renda
      ? Number(formData.renda.replace(/\s|R\$|\./g, "").replace(",", "."))
      : undefined;

    setLoading(true);

    const onMutationSuccess = (data: unknown) => {
      setLoading(false);
      onOpenChange(false);

      const state = {
        offers: data,
        selectedValue: valorNumber,
        selectedPrazo: Number(formData.prazo),
        prazoOptions,
        customerId,
        transactionId,
      };

      if (isSelfHireFlow && selfHireSimulate) {
        selfHireSimulate(state as unknown as LocationState);
      } else {
        navigate(`/transacoes/pan/${transactionId}`, {
          state,
        });
      }
    };

    const onMutationError = () => setLoading(false);

    if (isSelfHireFlow) {
      if (!linkName || !panData?.cpf) {
        setLoading(false);
        return;
      }
      generateOffersPanMutation.mutate(
        {
          linkName,
          payload: {
            cpf: panData.cpf,
            prazo: Number(formData.prazo),
            valor: valorNumber,
            renda: rendaNumber,
            proposalId: transactionId,
          },
        },
        { onSuccess: onMutationSuccess, onError: onMutationError },
      );
    } else {
      generateOffersMutation.mutate(
        {
          id: transactionId,
          payload: {
            valor: valorNumber,
            prazo: Number(formData.prazo),
            renda: rendaNumber,
            customerId,
          },
        },
        { onSuccess: onMutationSuccess, onError: onMutationError },
      );
    }
  });

  useEffect(() => {
    if (panData?.amount !== null && panData?.amount !== undefined) {
      const amountInReais = panData.amount / 100;

      const formattedAmount = amountInReais.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      form.setValue("value", formattedAmount);
    }
  }, [panData, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex flex-row items-start justify-between border-b pb-8">
          <div>
            <DialogTitle className="text-xl font-semibold text-black01">
              Simulação PAN CLT
            </DialogTitle>
            <DialogDescription className="!mt-0 text-base text-black04">
              Preencha os campos para gerar as ofertas
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSimulate}>
          <FormProvider {...form}>
            <div className="flex flex-col space-y-4">
              <BasicInput
                name="value"
                label="Valor da parcela"
                error={errors.value?.message}
                onInput={moneyMask}
              />
              <BasicInput
                name="renda"
                label="Renda mensal"
                error={errors.renda?.message}
                onInput={moneyMask}
              />
              <BasicSelect
                name="prazo"
                label="Prazo"
                error={errors.prazo?.message}
                options={prazoOptions}
                placeholder="Selecione"
              />
            </div>
            <DialogFooter className="mt-10">
              <DialogClose>
                <Button
                  variant="outline"
                  className="w-[160px] rounded-full text-primary"
                >
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                className="w-[160px] rounded-full font-semibold text-white"
                isLoading={loading}
                disabled={loading}
              >
                Simular
              </Button>
            </DialogFooter>
          </FormProvider>
        </form>
      </DialogContent>
    </Dialog>
  );
};
